import React from "react";
import {
  constrainToBounds,
  fitScaleForBounds,
  focusTargetRect,
  type MapBounds,
  type MapTransform,
  mapBoundsToSvgPixels,
  maxZoomForBounds,
  tiltZoomForBounds,
  translateToCenter
} from "./mapBounds";

export interface UseSvgMapInteractionsOptions {
  allowInteractionsWhenFocussed?: boolean;
  tiltMax?: number;
  desiredZoom?: number;
  /**
   * When set, focusing a resource/point animates to exactly this scale (zooming
   * in OR out), instead of only zooming in when below `desiredZoom`. Used by
   * embedded previews to frame the target at a fixed zoom.
   */
  exactZoom?: number;
  maxZoom?: number;
  minZoom?: number;
  zoomStep?: number;
  offsetTop?: number;
  offsetLeft?: number;
  doubleTapTimeout?: number;
  doubleTapDistance?: number;
  doubleTapZoomAmount?: number;
  tripleTapMode?: "fit" | "out";
  tripleTapZoomAmount?: number;
  enabled?: boolean;
  /**
   * In map (SVG user) units. When set, the map opens fitted to these bounds and
   * can't be zoomed out or panned beyond them.
   */
  bounds?: MapBounds | null;
}

export interface SvgMapInteractionsEventMap {
  transform: {
    tx: number;
    ty: number;
    scale: number;
    tilt: number;
  };
  ready: {};
  closed: {};
}

export interface FocusToResourceOptions {
  setTarget?: boolean;
  tiltMax?: number;
  onComplete?: () => void;
}

type SvgMapInteractionsEventType = keyof SvgMapInteractionsEventMap;

type EventListeners = {
  [K in SvgMapInteractionsEventType]: Set<
    (payload: SvgMapInteractionsEventMap[K]) => void
  >;
};

export interface UseSvgMapInteractionsReturn {
  updateCount: number;
  focusResource: (resourceId: string, options?: FocusToResourceOptions) => void;
  zoomToResource: (
    resourceId: string,
    options?: FocusToResourceOptions
  ) => void;
  focusPoint: (x: number, y: number, options?: FocusToResourceOptions) => void;
  blurResource: () => void;
  init: () => void;
  subscribe: <K extends keyof SvgMapInteractionsEventMap>(
    event: K,
    callback: (payload: SvgMapInteractionsEventMap[K]) => void
  ) => () => void;
  setMapReady: () => void;
  setMapClosed: () => void;
  unsubscribe: <K extends keyof SvgMapInteractionsEventMap>(
    event: K,
    callback: (payload: SvgMapInteractionsEventMap[K]) => void
  ) => void;
  svgRef: React.MutableRefObject<SVGSVGElement | null | undefined>;
  mapRef: React.MutableRefObject<HTMLDivElement | null | undefined>;
  containerRef: React.MutableRefObject<HTMLDivElement | null | undefined>;
  setTransform: (tx: number, ty: number, scale: number, tilt?: number) => void;
  centerAndFitMap: (options?: {
    animate?: boolean;
    replaceFocus?: boolean;
    keepView?: boolean;
  }) => MapTransform | undefined;
  zoomIn: () => void;
  zoomOut: () => void;
  currentTransform: React.MutableRefObject<{
    tx: number;
    ty: number;
    scale: number;
  }>;
  hasUserMoved: () => boolean;
}

type TransformMap = Record<string, string | string[]>;

// Room left on each side when fitting the map, as a fraction of the viewport
const FIT_PADDING = 0.025;

function parseTransformToObject(transform: string): TransformMap {
  const regex = /([a-zA-Z0-9]+)\(([^)]+)\)/g;
  const result: TransformMap = {};
  let match: RegExpExecArray | null;

  while ((match = regex.exec(transform)) !== null) {
    const fn = match[1];
    const values = match[2]
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);

    // Store single value as string, multiple as array
    result[fn] = values.length === 1 ? values[0] : values;
  }

  return result;
}

const getMidpoint = (touches: TouchList, containerRect: DOMRect) => {
  const ax = touches[0].clientX - containerRect.left;
  const ay = touches[0].clientY - containerRect.top;
  const bx = touches[1].clientX - containerRect.left;
  const by = touches[1].clientY - containerRect.top;
  return { x: (ax + bx) / 2, y: (ay + by) / 2 };
};

// Resolve a node's bounding box in the root SVG's user (map) coordinate
// system. Legacy overlay anchors carry absolute map coordinates in their
// markup, so their raw getBBox() is already map-space — but native
// map-builder spot anchors sit inside their spot group's translate/rotate,
// so getBBox() returns spot-LOCAL coordinates (small numbers near the
// origin), which made zoom-to-resource pan to the map's top-left corner.
// Mapping through node→svg screen CTMs handles both (identity for legacy);
// pan/zoom CSS transforms on shared HTML ancestors cancel out.
function getMapSpaceBBox(
  node: SVGGraphicsElement,
  svg: SVGSVGElement
): { x: number; y: number; width: number; height: number } {
  const bbox = node.getBBox();
  const nodeCTM = node.getScreenCTM();
  const svgCTM = svg.getScreenCTM();
  if (!nodeCTM || !svgCTM) return bbox;
  const toMap = svgCTM.inverse().multiply(nodeCTM);
  const corners = [
    new DOMPoint(bbox.x, bbox.y),
    new DOMPoint(bbox.x + bbox.width, bbox.y),
    new DOMPoint(bbox.x, bbox.y + bbox.height),
    new DOMPoint(bbox.x + bbox.width, bbox.y + bbox.height)
  ].map((point) => point.matrixTransform(toMap));
  const xs = corners.map((point) => point.x);
  const ys = corners.map((point) => point.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return {
    x: minX,
    y: minY,
    width: Math.max(...xs) - minX,
    height: Math.max(...ys) - minY
  };
}

export function useSvgMapInteractions(
  svgRef: React.MutableRefObject<SVGSVGElement | null | undefined>,
  mapRef: React.MutableRefObject<HTMLDivElement | null | undefined>,
  containerRef: React.MutableRefObject<HTMLDivElement | null | undefined>,
  options?: UseSvgMapInteractionsOptions
): UseSvgMapInteractionsReturn {
  const listenersRef = React.useRef<Partial<EventListeners>>({});

  function subscribe<K extends SvgMapInteractionsEventType>(
    event: K,
    callback: (payload: SvgMapInteractionsEventMap[K]) => void
  ): () => void {
    const listeners = listenersRef.current[event] ?? new Set();
    listeners.add(callback);
    listenersRef.current[event] = listeners as EventListeners[K];

    return () => unsubscribe(event, callback);
  }

  function unsubscribe<K extends SvgMapInteractionsEventType>(
    event: K,
    callback: (payload: SvgMapInteractionsEventMap[K]) => void
  ) {
    const listeners = listenersRef.current[event];
    listeners?.delete(callback);
    if (listeners && listeners.size === 0) {
      delete listenersRef.current[event];
    }
  }

  function dispatch<K extends SvgMapInteractionsEventType>(
    event: K,
    payload: SvgMapInteractionsEventMap[K]
  ) {
    listenersRef.current[event]?.forEach((cb) => {
      (cb as (payload: SvgMapInteractionsEventMap[K]) => void)(payload);
    });
  }

  const allowInteractionsWhenFocussed =
    options?.allowInteractionsWhenFocussed ?? true;
  const isDragging = React.useRef(false);
  const hasTilt = React.useRef(false);
  const lastMouse = React.useRef({ x: 0, y: 0 });
  const currentTransform = React.useRef({ tx: 0, ty: 0, scale: 1 });
  const currentTilt = React.useRef<number>(0);
  const lastTapRef = React.useRef<{
    t: number;
    x: number;
    y: number;
    count: number;
  } | null>(null);
  const touchMovedRef = React.useRef(false);

  const [update, setUpdate] = React.useState(0);
  const [focussedTarget, setFocussedTarget] = React.useState<
    SVGSVGElement | null | undefined
  >(null);

  const tiltMax = options?.tiltMax ?? 15;
  const desiredZoom = options?.desiredZoom ?? 3;
  const exactZoom = options?.exactZoom;
  const maxZoom = options?.maxZoom ?? 2.8;
  let minZoom = options?.minZoom ?? 0.05;
  const zoomStep = options?.zoomStep ?? 0.1;
  const doubleTapTimeout = options?.doubleTapTimeout ?? 300; // ms
  const doubleTapDistance = options?.doubleTapDistance ?? 20; // px radius
  const doubleTapZoomAmount = options?.doubleTapZoomAmount ?? 0.6; // scale delta on double tap
  const tripleTapMode = options?.tripleTapMode ?? "out"; // 'fit' | 'out'
  const tripleTapZoomAmount = options?.tripleTapZoomAmount ?? 1.2; // used if mode==='out'

  // The transform blurResource returns to, saved when a resource is focused. A ref, so a fit
  // followed by a focus in the same tick saves the fitted transform.
  const previousTransform = React.useRef<{
    tx: number;
    ty: number;
    scale: number;
  } | null>(null);

  // The running animation. Starting another one, a fit or a gesture cancels it
  // so two loops never write the transform at once; onCancel undoes its side effects.
  const animation = React.useRef<{
    frame: number;
    onCancel?: () => void;
  } | null>(null);
  const cancelAnimation = () => {
    const running = animation.current;
    if (!running) return;
    animation.current = null;
    cancelAnimationFrame(running.frame);
    running.onCancel?.();
  };
  const nextFrame = (step: FrameRequestCallback, onCancel?: () => void) => {
    const frame = requestAnimationFrame((time) => {
      if (animation.current?.frame === frame) animation.current = null;
      step(time);
    });
    animation.current = { frame, onCancel };
  };
  const restoreScroll = () => {
    document.body.style.overflow = "auto";
  };

  // Whether the person panned or zoomed since the last fit
  const userMoved = React.useRef(false);

  const boundsRef = React.useRef(options?.bounds ?? null);
  boundsRef.current = options?.bounds ?? null;

  // The visible map area. The containerRef may point to the outer
  // floor-container which includes the sidebar area; the map is displayed
  // inside .map-container which has the sidebar offset applied, so that one
  // is preferred when present.
  const getViewportDimensions = () => {
    const map = mapRef.current;
    const container = containerRef.current;
    const mapContainer =
      container?.querySelector(".map-container") ||
      map?.closest(".map-container");
    const target = mapContainer || container;
    const rect = target?.getBoundingClientRect();
    if (rect && rect.width > 0 && rect.height > 0) {
      return { width: rect.width, height: rect.height };
    }
    return { width: window.innerWidth, height: window.innerHeight };
  };

  // The bounds option in the map element's own pixels, or null when unset
  // Zone bounds with an area: without them the map behaves as before zones
  const hasZoneBounds = () => {
    const bounds = boundsRef.current;
    return !!bounds && bounds.width > 0 && bounds.height > 0;
  };

  const getPixelBounds = (): MapBounds | null => {
    const bounds = boundsRef.current;
    const svg = svgRef.current;
    if (!bounds || !svg || !hasZoneBounds()) return null;
    return mapBoundsToSvgPixels(svg, bounds);
  };

  // With bounds, zooming is relative to the fitted scale, which can be above
  // the default maximum for a small zone
  // With zone bounds, transforms are in the map element's pixels, so focus
  // targets are converted like the bounds. Without bounds they stay in SVG
  // user units, as every other map always did.
  const toFocusSpace = (
    svg: SVGSVGElement | null,
    rect: MapBounds
  ): MapBounds => (svg ? focusTargetRect(svg, rect, hasZoneBounds()) : rect);

  const getMaxZoom = () => {
    const bounds = getPixelBounds();
    if (!bounds) return maxZoom;
    return maxZoomForBounds(
      maxZoom,
      fitScaleForBounds(bounds, getViewportDimensions(), FIT_PADDING)
    );
  };

  // The zoom from which the map tilts (and to which focusing zooms). With
  // bounds it sits above the fitted scale, so the zone's overview stays flat
  const getTiltZoom = () => {
    const bounds = getPixelBounds();
    if (!bounds) return desiredZoom;
    return tiltZoomForBounds(
      desiredZoom,
      fitScaleForBounds(bounds, getViewportDimensions(), FIT_PADDING)
    );
  };

  const constrain = (transform: MapTransform): MapTransform => {
    const bounds = getPixelBounds();
    if (!bounds) return transform;
    return constrainToBounds(
      transform,
      bounds,
      getViewportDimensions(),
      FIT_PADDING
    );
  };

  const applyTransform = (
    rawTx: number,
    rawTy: number,
    rawScale: number,
    setTilt?: number
  ) => {
    const map = mapRef.current;
    const container = containerRef.current;
    if (!map) return;

    const { tx, ty, scale } = constrain({
      tx: rawTx,
      ty: rawTy,
      scale: rawScale
    });

    if (setTilt !== 0) {
      if (setTilt) {
        currentTilt.current = setTilt;
      } else {
        currentTilt.current = scale >= getTiltZoom() ? tiltMax : 0;
      }
    }

    const tilt = currentTilt.current;
    const _container = container?.querySelector(".item-container") as
      | HTMLDivElement
      | undefined;

    if (_container) {
      _container.style.transform = `perspective(1000px) rotateX(${currentTilt.current}deg)`;
    }

    map.style.transform = `

      translate(${tx}px, ${ty}px)
      scale(${scale})
    `
      .trim()
      .replace(/\s+/g, " ");

    map.style.filter =
      tilt > 0 ? "drop-shadow(0 20px 40px rgba(0, 0, 0, 0.4))" : "none";

    dispatch("transform", { tx, ty, scale, tilt });
  };

  React.useEffect(() => {
    if (options?.enabled === false) return;

    const map = mapRef.current;
    const mapContainer = map?.closest(".item-container") as HTMLDivElement;
    const container = containerRef.current;
    if (!map || !container || !mapContainer) return;

    // The outer map-container covers the full viewport area and is the
    // right target for mousedown/touchstart so users can pan from the
    // background, not only from over the map content.
    const panContainer =
      (map?.closest(".map-container") as HTMLDivElement) || container;

    if (!allowInteractionsWhenFocussed && focussedTarget) {
      return;
    }

    // Disable native scroll/overscroll inside the map area (important on iOS)
    const prevTouchAction = container.style.touchAction;
    const prevOverflow = container.style.overflow;
    const prevOverscroll = (container.style as any).overscrollBehavior;
    (container.style as any).touchAction = "none"; // iOS 13+ supports this
    container.style.overflow = "hidden"; // no internal scroll
    (container.style as any).overscrollBehavior = "contain"; // Chrome/Android/desktop; harmless on iOS

    let pinchStart: {
      distance: number; // starting finger distance
      scale: number; // starting scale
      tx: number; // starting tx
      ty: number; // starting ty
      midX: number; // midpoint (relative to container)
      midY: number;
    } | null = null;

    const getTouchPosition = (touch: Touch) => ({
      x: touch.clientX,
      y: touch.clientY
    });

    const getTouchDistance = (touches: TouchList) => {
      const [a, b] = [touches[0], touches[1]];
      return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
    };

    const handleMouseDown = (e: MouseEvent) => {
      const map = mapRef.current;
      if (map) {
        const _currentTransform = parseTransformToObject(map.style.transform);
        if (_currentTransform.scale) {
          currentTransform.current.scale = parseFloat(
            _currentTransform.scale as string
          );
        }
      }
      isDragging.current = true;
      lastMouse.current = { x: e.clientX, y: e.clientY };
      e.preventDefault();
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      cancelAnimation();

      const dx = e.clientX - lastMouse.current.x;
      const dy = e.clientY - lastMouse.current.y;
      lastMouse.current = { x: e.clientX, y: e.clientY };

      const { tx, ty, scale } = currentTransform.current;
      const newTx = tx + dx;
      const newTy = ty + dy;

      userMoved.current = true;
      currentTransform.current = constrain({ tx: newTx, ty: newTy, scale });
      applyTransform(newTx, newTy, scale);
    };

    const handleMouseUp = () => {
      isDragging.current = false;
    };

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (!map || !container) return;
      cancelAnimation();

      // Normalize wheel delta to handle both mouse wheel and trackpad
      // Mouse wheel: typically deltaMode=1 (LINE) or deltaMode=0 with large values (~100)
      // Trackpad: typically deltaMode=0 (PIXEL) with small values (~1-20), many rapid events
      let normalizedDelta: number;

      if (e.deltaMode === 1) {
        // DOM_DELTA_LINE (mouse wheel) - use direction with full zoomStep
        normalizedDelta = e.deltaY < 0 ? 1 : -1;
      } else {
        // DOM_DELTA_PIXEL (trackpad or high-precision mouse)
        // Trackpads fire many rapid events with small deltas (typically 1-20px each)
        // Mouse wheels in pixel mode have larger deltas (~100px)
        const absDelta = Math.abs(e.deltaY);

        if (absDelta < 50) {
          // Likely trackpad - use very low sensitivity for smooth, controlled zoom
          const trackpadSensitivity = 0.002;
          normalizedDelta = -e.deltaY * trackpadSensitivity;
        } else {
          // Likely mouse wheel in pixel mode - use direction-based zoom
          normalizedDelta = e.deltaY < 0 ? 1 : -1;
        }
      }

      const { tx, ty, scale } = currentTransform.current;

      let newScale = scale + normalizedDelta * zoomStep;
      newScale = Math.max(minZoom, Math.min(getMaxZoom(), newScale));
      if (newScale === scale) return;

      const containerRect = container.getBoundingClientRect();
      const mouseX = e.clientX - containerRect.left;
      const mouseY = e.clientY - containerRect.top;

      const mapRect = map.getBoundingClientRect();
      const centerX = mapRect.left - containerRect.left;
      const centerY = mapRect.top - containerRect.top;

      const dx = mouseX - centerX;
      const dy = mouseY - centerY;
      const scaleChange = newScale / scale;

      const newTx = tx - dx * (scaleChange - 1);
      const newTy = ty - dy * (scaleChange - 1);

      currentTransform.current = constrain({
        tx: newTx,
        ty: newTy,
        scale: newScale
      });
      hasTilt.current = newScale >= getTiltZoom();

      userMoved.current = true;
      applyTransform(newTx, newTy, newScale);
    };

    const handleTouchStart = (e: TouchEvent) => {
      touchMovedRef.current = false;

      e.stopPropagation();

      if (e.touches.length === 1) {
        // --- single-finger: keep your tap/drag + double/triple-tap logic ---
        const touch = e.touches[0];
        const now = performance.now();
        const prev = lastTapRef.current;

        const isQuickNearTap =
          !!prev &&
          now - prev.t <= doubleTapTimeout &&
          Math.hypot(touch.clientX - prev.x, touch.clientY - prev.y) <=
            doubleTapDistance;
        if (isQuickNearTap) {
          const nextCount = (prev.count ?? 1) + 1;

          if (nextCount === 2) {
            // DOUBLE-TAP: zoom in at tap (or a bit out if already zoomed)
            e.preventDefault();
            const alreadyZoomed =
              currentTransform.current.scale >= getTiltZoom();
            zoomAtPoint(
              touch.clientX,
              touch.clientY,
              alreadyZoomed
                ? { direction: "out" }
                : {
                    targetScale: Math.min(
                      getMaxZoom(),
                      Math.max(
                        getTiltZoom(),
                        currentTransform.current.scale + doubleTapZoomAmount
                      )
                    )
                  }
            );

            // keep the record so a 3rd tap within the window triggers triple-tap
            lastTapRef.current = {
              t: now,
              x: touch.clientX,
              y: touch.clientY,
              count: nextCount
            };
            isDragging.current = false;
            return;
          }

          if (nextCount === 3) {
            // TRIPLE-TAP: zoom out / reset
            e.preventDefault();

            if (tripleTapMode === "fit") {
              centerAndFitMap();
            } else {
              // zoom out around tap point by a larger step
              zoomAtPoint(touch.clientX, touch.clientY, {
                direction: "out",
                targetScale: Math.max(
                  minZoom,
                  currentTransform.current.scale - tripleTapZoomAmount
                )
              });
            }

            // done—don’t chain beyond triple
            lastTapRef.current = null;
            isDragging.current = false;
            return;
          }

          // (Rare) 4th+ taps just update the record
          lastTapRef.current = {
            t: now,
            x: touch.clientX,
            y: touch.clientY,
            count: nextCount
          };
          isDragging.current = false;
          return;
        }

        // Not a quick/near subsequent tap: start new sequence
        isDragging.current = true;
        lastMouse.current = { x: touch.clientX, y: touch.clientY };
        lastTapRef.current = {
          t: now,
          x: touch.clientX,
          y: touch.clientY,
          count: 1
        };
        // ensure we're not in a pinch state
        pinchStart = null;
      } else if (e.touches.length === 2) {
        // --- two-finger pinch start ---
        const containerRect = container.getBoundingClientRect();
        const distance = getTouchDistance(e.touches);
        const mid = getMidpoint(e.touches, containerRect);
        const { tx, ty, scale } = currentTransform.current;

        pinchStart = {
          distance,
          scale,
          tx,
          ty,
          midX: mid.x,
          midY: mid.y
        };

        // prevent scroll/bounce during pinch
        e.preventDefault();

        // make sure we aren't also "dragging"
        isDragging.current = false;

        // pinch cancels the tap chain
        lastTapRef.current = null;
      }
    };

    // Touch MOVE
    const handleTouchMove = (e: TouchEvent) => {
      e.stopPropagation();

      if (e.touches.length === 1 && isDragging.current && !pinchStart) {
        e.preventDefault();

        const touch = e.touches[0];
        const pos = { x: touch.clientX, y: touch.clientY };
        const dx = pos.x - lastMouse.current.x;
        const dy = pos.y - lastMouse.current.y;

        // jitter guard so micro-moves don't cancel double-tap chain
        if (Math.hypot(dx, dy) > 6) touchMovedRef.current = true;

        lastMouse.current = pos;
        if (touchMovedRef.current) {
          cancelAnimation();
          userMoved.current = true;
        }
        const { tx, ty, scale } = currentTransform.current;
        const newTx = tx + dx;
        const newTy = ty + dy;
        currentTransform.current = constrain({ tx: newTx, ty: newTy, scale });
        applyTransform(newTx, newTy, scale);
        return;
      }

      if (e.touches.length === 2 && pinchStart) {
        // --- pinch-to-zoom anchored at gesture midpoint ---
        e.preventDefault();
        e.stopPropagation();

        const newDistance = getTouchDistance(e.touches);
        let newScale = (pinchStart.scale * newDistance) / pinchStart.distance;
        newScale = Math.max(minZoom, Math.min(getMaxZoom(), newScale));

        const scaleChange = newScale / pinchStart.scale;

        // Keep the midpoint fixed while scaling
        const newTx =
          pinchStart.midX - (pinchStart.midX - pinchStart.tx) * scaleChange;
        const newTy =
          pinchStart.midY - (pinchStart.midY - pinchStart.ty) * scaleChange;

        currentTransform.current = constrain({
          tx: newTx,
          ty: newTy,
          scale: newScale
        });
        hasTilt.current = newScale >= getTiltZoom();

        cancelAnimation();
        userMoved.current = true;
        applyTransform(newTx, newTy, newScale);
        return;
      }
    };

    // Touch END / CANCEL
    const handleTouchEnd = () => {
      isDragging.current = false;
      pinchStart = null; // ensure pinch state clears
      if (touchMovedRef.current) lastTapRef.current = null;
    };

    // Mouse Events — attach mousedown to panContainer (the full-area
    // .map-container) so users can start dragging from anywhere in the
    // background, not just over the map content.
    panContainer.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    container.addEventListener("wheel", handleWheel, { passive: false });

    // Touch Events — same: panContainer so pan starts from background
    panContainer.addEventListener("touchstart", handleTouchStart, {
      passive: false
    });
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd);
    window.addEventListener("touchcancel", handleTouchEnd);

    return () => {
      panContainer.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      container.removeEventListener("wheel", handleWheel);

      panContainer.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [
    mapRef,
    containerRef,
    svgRef,
    update,
    focussedTarget,
    allowInteractionsWhenFocussed,
    options?.enabled
  ]);

  React.useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const computed = getComputedStyle(map);
    const transform = computed.transform;

    if (!transform || transform === "none") return;

    if (transform.startsWith("matrix(")) {
      // matrix(a, b, c, d, tx, ty)
      const values = transform
        .replace("matrix(", "")
        .replace(")", "")
        .split(",")
        .map((v) => Number.parseFloat(v.trim()));

      const [a, , , d, tx, ty] = values;

      const scaleX = a;
      const scaleY = d;

      const scale = (scaleX + scaleY) / 2;

      if (scale < minZoom) {
        minZoom = scale;
      }

      currentTransform.current = constrain({
        tx,
        ty,
        scale
      });

      hasTilt.current = scale >= getTiltZoom();
      currentTilt.current = hasTilt.current ? tiltMax : 0;
    } else if (transform.startsWith("matrix3d(")) {
      console.warn(
        "matrix3d parsing not implemented — using default transform"
      );
    }
  }, [
    mapRef,
    containerRef,
    svgRef,
    update,
    focussedTarget,
    allowInteractionsWhenFocussed
  ]);

  function focusResource(
    resourceId: string,
    focusOptions?: FocusToResourceOptions
  ) {
    const _titlMax = focusOptions?.tiltMax ?? tiltMax ?? 0;
    // Scope the anchor lookup to THIS map instance: several maps can be
    // mounted at once (e.g. an embedded preview in a modal above /map),
    // each rendering its own `interactive-{id}` node. A document-wide
    // getElementById grabs whichever comes first in the DOM — focusing
    // (and mutating) another map on the page. Legacy maps keep a
    // container-level fallback because their anchors live in a sibling
    // overlay svg rather than inside svgRef; document is the last resort.
    const anchorSelector = `[id="interactive-${resourceId}"]`;
    const node = (svgRef.current?.querySelector(anchorSelector) ??
      containerRef.current?.querySelector(anchorSelector) ??
      document.getElementById(
        `interactive-${resourceId}`
      )) as SVGSVGElement | null;
    if (!node || !svgRef.current || !mapRef.current || !containerRef.current)
      return;

    cancelAnimation();
    if (focusOptions?.setTarget !== false) setFocussedTarget(node);

    document.body.style.overflow = "hidden";

    const svg = svgRef.current;
    const map = mapRef.current;
    const container = containerRef.current?.querySelector(".item-container");

    const {
      tx: startTx,
      ty: startTy,
      scale: currentScale
    } = currentTransform.current;

    if (!previousTransform.current) {
      previousTransform.current = currentTransform.current;
    }

    const doZoom = currentScale < getTiltZoom() && !hasTilt.current;
    const finalScale = exactZoom ?? (doZoom ? getTiltZoom() : currentScale);

    // In the map element's own pixels (the space of tx/ty), like the zone
    // bounds fit and constrain use: SVG user units only match those pixels when
    // the viewBox has no origin and the element is drawn at the viewBox's size
    const targetRect = toFocusSpace(svg, getMapSpaceBBox(node, svg));

    // Centre on the visible map area, resolved the same way `centerAndFitMap`
    // does (the `.map-container`, else the root container). The `.item-container`
    // queried above is the perspective host, and on some layouts (e.g. the
    // wayfinder kiosk) it shrink-wraps the whole floorplan — so its box is far
    // larger than, and off-centre from, the viewport on big non-square maps.
    // Centring off that box pushed the focused node off-centre.
    const dimsTarget =
      containerRef.current?.querySelector(".map-container") ||
      map?.closest(".map-container") ||
      containerRef.current;
    const containerRect = dimsTarget?.getBoundingClientRect() || {
      width: window.innerWidth,
      height: window.innerHeight
    };

    const containerCenterX = containerRect.width / 2;
    const containerCenterY = containerRect.height / 2;

    // Pin the 3D tilt's rotation axis (and perspective vanishing point) to the
    // exact point the node is centred on. Otherwise `rotateX` pivots around
    // `.item-container`'s default `50% 50%` — the centre of the whole floorplan
    // — which on a big non-square map sits far from the centred node, so the
    // perspective projection drags it off-centre as the tilt animates in. No
    // visible effect when tiltMax is 0 (e.g. the main /map view).
    if (container instanceof HTMLElement) {
      const origin = `${containerCenterX}px ${containerCenterY}px`;
      container.style.transformOrigin = origin;
      container.style.perspectiveOrigin = origin;
    }

    const { tx: targetTx, ty: targetTy } = translateToCenter(
      targetRect,
      finalScale,
      { width: containerRect.width, height: containerRect.height },
      { left: options?.offsetLeft, top: options?.offsetTop }
    );

    const duration = 500;
    const startTime = performance.now();

    function easeInOut(t: number) {
      return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    }

    function animate(time: number) {
      const t = Math.min((time - startTime) / duration, 1);
      const eased = easeInOut(t);

      const currentTx = startTx + (targetTx - startTx) * eased;
      const currentTy = startTy + (targetTy - startTy) * eased;
      const currentZoom = currentScale + (finalScale - currentScale) * eased;

      applyTransform(
        currentTx,
        currentTy,
        currentZoom,
        doZoom && _titlMax > 0
          ? _titlMax * (1 - Math.cos(eased * Math.PI))
          : hasTilt.current
            ? _titlMax
            : 0
      );

      currentTransform.current = constrain({
        tx: currentTx,
        ty: currentTy,
        scale: currentZoom
      });

      if (doZoom) hasTilt.current = true;

      if (t < 1) {
        nextFrame(animate, restoreScroll);
      } else {
        document.body.style.overflow = "auto";
        focusOptions?.onComplete?.();
      }
    }

    nextFrame(animate, restoreScroll);
  }

  function zoomToResource(
    resourceId: string,
    focusOptions?: FocusToResourceOptions
  ) {
    focusResource(resourceId, {
      setTarget: false,
      tiltMax: 0,
      ...focusOptions
    });
  }

  // focusPoint mirrors focusResource but centres/zooms on a bare map coordinate
  // (a zero-size synthetic target) instead of a resource's SVG node — used to
  // focus a pinned point that has no interactive element.
  function focusPoint(
    x: number,
    y: number,
    focusOptions?: FocusToResourceOptions
  ) {
    const _titlMax = focusOptions?.tiltMax ?? tiltMax ?? 0;
    if (!svgRef.current || !mapRef.current || !containerRef.current) return;

    cancelAnimation();
    document.body.style.overflow = "hidden";

    const map = mapRef.current;
    const container = containerRef.current?.querySelector(".item-container");

    const {
      tx: startTx,
      ty: startTy,
      scale: currentScale
    } = currentTransform.current;

    if (!previousTransform.current) {
      previousTransform.current = currentTransform.current;
    }

    const doZoom = currentScale < getTiltZoom() && !hasTilt.current;
    const finalScale = exactZoom ?? (doZoom ? getTiltZoom() : currentScale);

    const targetRect = toFocusSpace(svgRef.current, {
      x,
      y,
      width: 0,
      height: 0
    });

    const dimsTarget =
      containerRef.current?.querySelector(".map-container") ||
      map?.closest(".map-container") ||
      containerRef.current;
    const containerRect = dimsTarget?.getBoundingClientRect() || {
      width: window.innerWidth,
      height: window.innerHeight
    };

    const containerCenterX = containerRect.width / 2;
    const containerCenterY = containerRect.height / 2;

    if (container instanceof HTMLElement) {
      const origin = `${containerCenterX}px ${containerCenterY}px`;
      container.style.transformOrigin = origin;
      container.style.perspectiveOrigin = origin;
    }

    const { tx: targetTx, ty: targetTy } = translateToCenter(
      targetRect,
      finalScale,
      { width: containerRect.width, height: containerRect.height },
      { left: options?.offsetLeft, top: options?.offsetTop }
    );

    const duration = 500;
    const startTime = performance.now();

    function easeInOut(t: number) {
      return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    }

    function animate(time: number) {
      const t = Math.min((time - startTime) / duration, 1);
      const eased = easeInOut(t);

      const currentTx = startTx + (targetTx - startTx) * eased;
      const currentTy = startTy + (targetTy - startTy) * eased;
      const currentZoom = currentScale + (finalScale - currentScale) * eased;

      applyTransform(
        currentTx,
        currentTy,
        currentZoom,
        doZoom && _titlMax > 0
          ? _titlMax * (1 - Math.cos(eased * Math.PI))
          : hasTilt.current
            ? _titlMax
            : 0
      );

      currentTransform.current = constrain({
        tx: currentTx,
        ty: currentTy,
        scale: currentZoom
      });

      if (doZoom) hasTilt.current = true;

      if (t < 1) {
        nextFrame(animate, restoreScroll);
      } else {
        document.body.style.overflow = "auto";
        focusOptions?.onComplete?.();
      }
    }

    nextFrame(animate, restoreScroll);
  }

  const blurResource = () => {
    const map = mapRef.current;
    if (!map || !previousTransform.current) return;
    cancelAnimation();

    const {
      tx: startTx,
      ty: startTy,
      scale: startScale
    } = currentTransform.current;

    const {
      tx: targetTx,
      ty: targetTy,
      scale: targetScale
    } = previousTransform.current;

    const tiltStart = currentTilt.current;
    const tiltTarget = targetScale >= getTiltZoom() ? tiltMax : 0;
    // Interrupted (by a focus, a fit or a gesture), the resource is still let go
    const finishBlur = () => {
      restoreScroll();
      setFocussedTarget(null);
    };

    const duration = 500;
    const startTime = performance.now();

    function easeInOut(t: number) {
      return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    }

    function animate(time: number) {
      const t = Math.min((time - startTime) / duration, 1);
      const eased = easeInOut(t);

      const currentTx = startTx + (targetTx - startTx) * eased;
      const currentTy = startTy + (targetTy - startTy) * eased;
      const currentScale = startScale + (targetScale - startScale) * eased;
      const _currentTilt = tiltStart + (tiltTarget - tiltStart) * eased;

      applyTransform(currentTx, currentTy, currentScale, _currentTilt);

      document.body.style.overflow = "hidden";

      if (t < 1) {
        nextFrame(animate, finishBlur);
      } else {
        document.body.style.overflow = "auto";

        currentTransform.current = constrain({
          tx: targetTx,
          ty: targetTy,
          scale: targetScale
        });
        currentTilt.current = tiltTarget;
        hasTilt.current = targetScale >= getTiltZoom();
        previousTransform.current = null;
        setFocussedTarget(null);
      }
    }

    nextFrame(animate, finishBlur);
  };

  const setMapReady = () => {
    dispatch("ready", {});
  };

  const setMapClosed = () => {
    dispatch("closed", {});
  };

  const setTransform = (tx: number, ty: number, scale: number) => {
    cancelAnimation();
    currentTransform.current = constrain({ tx, ty, scale });
    applyTransform(tx, ty, scale);
  };

  /**
   * `replaceFocus`: the fit replaces any focus, so a later focus saves the fitted
   * transform to return to (otherwise blur still returns to the pre-focus view).
   * `keepView`: the view isn't moved, the fit only becomes what blur returns to.
   * Returns the fitted transform.
   */
  const centerAndFitMap = (options?: {
    animate?: boolean;
    replaceFocus?: boolean;
    keepView?: boolean;
  }): MapTransform | undefined => {
    const shouldAnimate = options?.animate !== false;
    const svg = svgRef.current;
    const map = mapRef.current;
    const container = containerRef.current;

    if (!svg || !map || !container) {
      console.warn("Required refs not available for centerAndFitMap");
      return;
    }

    // Get SVG content bounding box (position + size)
    const getSvgContentBounds = () => {
      try {
        const bbox = svg.getBBox();
        if (bbox.width > 0 && bbox.height > 0) {
          return {
            x: bbox.x,
            y: bbox.y,
            width: bbox.width,
            height: bbox.height
          };
        }
      } catch (error) {
        console.warn("Failed to get SVG bounding box:", error);
      }

      // Fallback: use SVG viewBox or default dimensions
      const viewBox = svg.viewBox.baseVal;
      if (viewBox && viewBox.width > 0 && viewBox.height > 0) {
        return {
          x: viewBox.x,
          y: viewBox.y,
          width: viewBox.width,
          height: viewBox.height
        };
      }

      // Last resort: use SVG element dimensions
      return {
        x: 0,
        y: 0,
        width: svg.clientWidth || 1000,
        height: svg.clientHeight || 1000
      };
    };

    const containerDims = getViewportDimensions();
    const svgBounds = getPixelBounds() ?? getSvgContentBounds();

    // Calculate scale to fit with small padding (5% on each side)
    const padding = FIT_PADDING;
    const availableWidth = containerDims.width * (1 - 2 * padding);
    const availableHeight = containerDims.height * (1 - 2 * padding);

    const scaleX = availableWidth / svgBounds.width;
    const scaleY = availableHeight / svgBounds.height;

    // Use the smaller scale to fit entirely
    let targetScale = Math.min(scaleX, scaleY);

    // Clamp to allowed range
    targetScale = Math.max(minZoom, Math.min(getMaxZoom(), targetScale));

    // Calculate center position, accounting for content offset within SVG
    const scaledWidth = svgBounds.width * targetScale;
    const scaledHeight = svgBounds.height * targetScale;

    const targetTx =
      (containerDims.width - scaledWidth) / 2 - svgBounds.x * targetScale;
    const targetTy =
      (containerDims.height - scaledHeight) / 2 - svgBounds.y * targetScale;

    const targetTilt = targetScale >= getTiltZoom() ? tiltMax : 0;
    const fit = constrain({ tx: targetTx, ty: targetTy, scale: targetScale });

    if (options?.keepView) {
      previousTransform.current = fit;
      return fit;
    }
    cancelAnimation();
    userMoved.current = false;
    if (options?.replaceFocus) {
      previousTransform.current = null;
    }

    if (!shouldAnimate) {
      applyTransform(targetTx, targetTy, targetScale, targetTilt);
      currentTransform.current = fit;
      currentTilt.current = targetTilt;
      hasTilt.current = targetScale >= getTiltZoom();
      return fit;
    }

    // Get current transform for animation
    const current = currentTransform.current;
    const startTx = Number.isFinite(current.tx) ? current.tx : 0;
    const startTy = Number.isFinite(current.ty) ? current.ty : 0;
    const startScale = Number.isFinite(current.scale) ? current.scale : 1;

    // Animate to target
    const duration = 500;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function
      const eased =
        progress < 0.5
          ? 2 * progress * progress
          : // biome-ignore lint/style/useExponentiationOperator: <explanation>
            1 - Math.pow(-2 * progress + 2, 2) / 2;

      // Interpolate values
      const tx = startTx + (targetTx - startTx) * eased;
      const ty = startTy + (targetTy - startTy) * eased;
      const scale = startScale + (targetScale - startScale) * eased;

      // Calculate tilt
      const tilt =
        currentTilt.current + (targetTilt - currentTilt.current) * eased;

      // Apply transform
      applyTransform(tx, ty, scale, tilt);

      // Update current state
      currentTransform.current = constrain({ tx, ty, scale });

      // Continue animation or finish
      if (progress < 1) {
        nextFrame(animate);
      } else {
        // Final state
        currentTilt.current = targetTilt;
        hasTilt.current = targetScale >= getTiltZoom();
      }
    };

    nextFrame(animate);
    return fit;
  };

  function zoomAtPoint(
    clientX: number,
    clientY: number,
    opts: {
      direction?: "in" | "out";
      targetScale?: number;
      duration?: number;
    } = {}
  ) {
    const map = mapRef.current;
    const container = containerRef.current;
    if (!map || !container) return;

    const {
      tx: startTx,
      ty: startTy,
      scale: startScale
    } = currentTransform.current;
    const containerRect = container.getBoundingClientRect();

    // point relative to container
    const px = clientX - containerRect.left;
    const py = clientY - containerRect.top;

    // choose target scale
    const targetScale =
      typeof opts.targetScale === "number"
        ? opts.targetScale
        : (() => {
            const dir = opts.direction ?? "in";
            const step = doubleTapZoomAmount;
            const raw = startScale + (dir === "in" ? +step : -step);
            return Math.max(minZoom, Math.min(getMaxZoom(), raw));
          })();

    if (targetScale === startScale) return;
    cancelAnimation();
    userMoved.current = true;

    // keep the tap point fixed during zoom
    const scaleChange = targetScale / startScale;
    const targetTx = px - (px - startTx) * scaleChange;
    const targetTy = py - (py - startTy) * scaleChange;

    // animate
    const duration = opts.duration ?? 250;
    const startTime = performance.now();
    const startTilt = currentTilt.current;
    const targetTilt = targetScale >= getTiltZoom() ? tiltMax : 0;

    const animate = (now: number) => {
      const t = Math.min((now - startTime) / duration, 1);
      const eased =
        t < 0.5
          ? 2 * t * t
          : 1 -
            // biome-ignore lint/style/useExponentiationOperator: <explanation>
            Math.pow(-2 * t + 2, 2) / 2;

      const tx = startTx + (targetTx - startTx) * eased;
      const ty = startTy + (targetTy - startTy) * eased;
      const s = startScale + (targetScale - startScale) * eased;
      const tilt = startTilt + (targetTilt - startTilt) * eased;

      applyTransform(tx, ty, s, tilt);
      currentTransform.current = constrain({ tx, ty, scale: s });

      if (t < 1) nextFrame(animate);
      else {
        currentTilt.current = targetTilt;
        hasTilt.current = targetScale >= getTiltZoom();
      }
    };
    nextFrame(animate);
  }

  const zoomAtContainerCenter = (
    direction: "in" | "out",
    zoomAmount?: number
  ) => {
    const map = mapRef.current;
    const container = containerRef.current;
    if (!map || !container) return;

    const {
      tx: startTx,
      ty: startTy,
      scale: startScale
    } = currentTransform.current;

    const delta = direction === "in" ? 1 : -1;
    const amount = zoomAmount ?? 0.5;
    let targetScale = startScale + delta * amount;
    targetScale = Math.max(minZoom, Math.min(getMaxZoom(), targetScale));
    if (targetScale === startScale) return;
    cancelAnimation();
    userMoved.current = true;

    const containerRect = container.getBoundingClientRect();
    const centerX = containerRect.width / 2;
    const centerY = containerRect.height / 2;

    // Compute how to keep the center in place during zoom
    const scaleChange = targetScale / startScale;
    const targetTx = centerX - (centerX - startTx) * scaleChange;
    const targetTy = centerY - (centerY - startTy) * scaleChange;

    // Animate the zoom
    const duration = 300; // Shorter duration for zoom animations
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function - use a snappier curve for zoom
      const eased =
        progress < 0.5
          ? 2 * progress * progress
          : // biome-ignore lint/style/useExponentiationOperator: <explanation>
            1 - Math.pow(-2 * progress + 2, 2) / 2;

      // Interpolate values
      const tx = startTx + (targetTx - startTx) * eased;
      const ty = startTy + (targetTy - startTy) * eased;
      const scale = startScale + (targetScale - startScale) * eased;

      // Calculate tilt
      const targetTilt = targetScale >= getTiltZoom() ? tiltMax : 0;
      const tilt =
        currentTilt.current + (targetTilt - currentTilt.current) * eased;

      // Apply transform
      applyTransform(tx, ty, scale, tilt);

      // Update current state
      currentTransform.current = constrain({ tx, ty, scale });

      // Continue animation or finish
      if (progress < 1) {
        nextFrame(animate);
      } else {
        // Final state
        currentTilt.current = targetTilt;
        hasTilt.current = targetScale >= getTiltZoom();
      }
    };

    nextFrame(animate);
  };

  const zoomIn = (zoomAmount?: number) =>
    zoomAtContainerCenter("in", zoomAmount);
  const zoomOut = (zoomAmount?: number) =>
    zoomAtContainerCenter("out", zoomAmount);

  const init = () => {
    setUpdate((u) => u + 1);
    setTimeout(() => {
      const map = mapRef.current;
      if (map) {
        const _currentTransform = parseTransformToObject(map.style.transform);
        if (_currentTransform.scale) {
          currentTransform.current.scale = parseFloat(
            _currentTransform.scale as string
          );
        }
        if (_currentTransform.translate) {
          const translate = _currentTransform.translate;
          if (Array.isArray(translate) && translate.length === 2) {
            currentTransform.current.tx = parseFloat(translate[0] as string);
            currentTransform.current.ty = parseFloat(translate[1] as string);
          } else if (typeof translate === "string") {
            const parts = translate
              .split(" ")
              .map((p) => p.trim())
              .filter(Boolean);
            if (parts.length === 2) {
              currentTransform.current.tx = parseFloat(parts[0]);
              currentTransform.current.ty = parseFloat(parts[1]);
            }
          }
        }
      }
      dispatch("transform", {
        tx: currentTransform.current.tx,
        ty: currentTransform.current.ty,
        scale: currentTransform.current.scale,
        tilt: currentTilt.current
      });
    }, 500);
  };

  React.useEffect(() => {
    if (options?.enabled !== false) {
      init();
    }
  }, [options?.enabled]);

  return {
    focusResource,
    blurResource,
    zoomToResource,
    focusPoint,
    init,
    subscribe,
    unsubscribe,
    svgRef,
    mapRef,
    containerRef,
    setMapReady,
    setMapClosed,
    setTransform,
    zoomIn,
    zoomOut,
    centerAndFitMap,
    currentTransform,
    hasUserMoved: () => userMoved.current,
    updateCount: update
  };
}
